import type { NotificationCatalogEntry } from '@peles/shared';
import type {
  Channel,
  NotificationGroup,
  NotificationRule,
  RoutingPerson,
} from '../../api/notificationRouting';

/**
 * The "send to" choice that means "no rule here". A real id rather than an
 * empty string, which a picker reads as nothing chosen at all.
 */
export const DEFAULT_CHOICE = '__default__';

/** One choice in a notification's "Sends to" picker. */
export interface SendToChoice {
  id: string;
  label: string;
  subtitle: string;
}

export const CHANNEL_LABEL: Record<Channel, string> = { in_app: 'In app', email: 'Email' };

/** "3 people", "1 person", "Nobody yet". */
export function peopleCount(n: number): string {
  if (n === 0) return 'Nobody yet';
  return n === 1 ? '1 person' : `${n} people`;
}

/** "In app and email", "Email only". */
export function describeChannels(channels: readonly Channel[]): string {
  if (channels.length === 0) return 'Not sent';
  if (channels.length === 2) return 'In app and email';
  return `${CHANNEL_LABEL[channels[0]]} only`;
}

/**
 * What the default reaches, in a few words — the catalog's own sentence is
 * the full account and reads too long inside a picker.
 */
export function shortDefault(entry: NotificationCatalogEntry): string {
  switch (entry.key) {
    case 'inquiry.received':
      return 'Enquiry contacts';
    case 'task.office_summary':
      return 'Every administrator';
    default:
      return 'Everyone in the office';
  }
}

/**
 * The choices for one notification: the default first, then the groups.
 *
 * On an event, the default is whatever the office-wide rule says (so a rule
 * there reads "Same as office-wide: Money team"), and both the event's own
 * groups and the office-wide ones are offered.
 */
export function sendToChoices(input: {
  entry: NotificationCatalogEntry;
  isEvent: boolean;
  groups: readonly NotificationGroup[];
  officeGroups: readonly NotificationGroup[];
  officeRule: NotificationRule | null;
}): SendToChoice[] {
  const { entry, isEvent, groups, officeGroups, officeRule } = input;
  const inherited = officeRule
    ? officeGroups.find((group) => group.id === officeRule.groupId)
    : undefined;
  const defaultChoice: SendToChoice = inherited
    ? {
        id: DEFAULT_CHOICE,
        label: `Office-wide: ${inherited.name}`,
        subtitle: 'Same as Settings → Notifications',
      }
    : {
        id: DEFAULT_CHOICE,
        label: shortDefault(entry),
        subtitle: isEvent ? 'Default — same as Settings → Notifications' : 'Default',
      };
  const own = groups.map((group) => ({
    id: group.id,
    label: group.name,
    subtitle: `${isEvent ? 'This event' : 'Group'} · ${peopleCount(group.memberIds.length)}`,
  }));
  const office = isEvent
    ? officeGroups.map((group) => ({
        id: group.id,
        label: group.name,
        subtitle: `Office-wide · ${peopleCount(group.memberIds.length)}`,
      }))
    : [];
  return [defaultChoice, ...own, ...office];
}

/**
 * What a group actually receives here: the notifications whose rule in force
 * names it — this scope's own rule, else (on an event) the office-wide one.
 */
export function effectiveUses(
  groupId: string,
  entries: readonly NotificationCatalogEntry[],
  rules: readonly NotificationRule[],
  officeRules: readonly NotificationRule[],
): string[] {
  return entries
    .filter((entry) => {
      const rule =
        rules.find((r) => r.event === entry.key) ?? officeRules.find((r) => r.event === entry.key);
      return rule?.groupId === groupId;
    })
    .map((entry) => entry.label);
}

/** The catalog entries in their categories, in the order the catalog lists them. */
export function byCategory(
  entries: readonly NotificationCatalogEntry[],
): { category: string; entries: NotificationCatalogEntry[] }[] {
  const groups = new Map<string, NotificationCatalogEntry[]>();
  for (const entry of entries) {
    const list = groups.get(entry.category) ?? [];
    list.push(entry);
    groups.set(entry.category, list);
  }
  return [...groups].map(([category, list]) => ({ category, entries: list }));
}

/** A member by id, from the people list or the names resolved for those it no longer offers. */
export function personFor(
  id: string,
  people: readonly RoutingPerson[],
  resolved: readonly RoutingPerson[],
): RoutingPerson {
  return (
    people.find((person) => person.id === id) ??
    resolved.find((person) => person.id === id) ?? {
      id,
      name: 'Someone no longer here',
      detail: '',
      isClient: false,
    }
  );
}

/**
 * What deleting a group changes, in words: the notifications here that go
 * back to their default (or, on an event, to the office-wide rule), and how
 * many other events' rules go with it.
 */
export function deleteGroupMessage(input: {
  usage: readonly { event: string; eventId: string | null }[];
  eventId: string | null;
  catalog: readonly NotificationCatalogEntry[];
}): string {
  const { usage, eventId, catalog } = input;
  const here = usage
    .filter((use) => use.eventId === eventId)
    .map((use) => catalog.find((entry) => entry.key === use.event)?.label ?? use.event);
  const elsewhere = new Set(
    usage
      .filter((use) => use.eventId !== null && use.eventId !== eventId)
      .map((use) => use.eventId),
  ).size;
  const parts: string[] = [];
  if (here.length > 0) {
    parts.push(
      `${here.join(', ')} will go back to ${eventId ? 'the office-wide rule' : 'their default'}.`,
    );
  }
  if (elsewhere > 0) {
    parts.push(
      `${elsewhere === 1 ? 'One event uses' : `${elsewhere} events use`} it too; ${
        elsewhere === 1 ? 'that event goes' : 'those go'
      } back to the office-wide rule.`,
    );
  }
  return parts.length > 0 ? parts.join(' ') : 'It is not chosen for any notification.';
}
